export const getColumnKey = (col) =>
  typeof col === "string" ? col : col?.key || col?.id || col?.field || "";

/** Available-field keys that expand into multiple export/PDF columns. */
export const FIELD_EXPORT_COLUMN_MAP = {
  nextOfKinName: ["nextOfKinName", "nextOfKinContact", "nextOfKinRelation"],
  refereeName: ["refereeName", "refereePosition", "refereeContact"],
  driversLicenseClass: [
    "driversLicenseState",
    "driversLicenseClass",
    "driversLicenseConditions",
    "driversLicenseNumber",
    "driversLicenseExpiry",
    "uploadDrivingLicence",
  ],
  passportExpiryDate: [
    "passportNationality",
    "passportNumber",
    "passportExpiryDate",
    "uploadPassport",
  ],
  photoAgeExpiryDate: [
    "photoAgeState",
    "photoAgeUploaded",
    "photoAgeIdNumber",
    "photoAgeExpiryDate",
  ],
  uploadQualification: [
    "uploadQualification",
    "qualificationState",
    "qualificationMemberId",
    "qualificationExpiryDate",
    "qualificationIssueDate",
    "qualificationVerificationStatus",
  ],
  voc: ["voc", "vocType", "vocIssued", "vocUploaded"],
  policeClearanceUploaded: [
    "policeClearanceUploaded",
    "policeClearanceCertifyingAuthority",
    "policeClearanceIssueDate",
  ],
  visaUpload: [
    "visaType",
    "visaGrantNumber",
    "visaExpiryDate",
    "visaIssueDate",
    "visaUpload",
  ],
  medicalAssessments: ["medicalAssessments", "medicalIssueDate", "medicalUpload"],
  ppeBootsSize: ["ppeShirtSize", "ppePantsSize", "ppeBootsSize"],
  lslState: ["lslState", "lslMemberNumber"],
};

/** Flatten selected fields into unique export column keys (parents expand). */
export const expandExportColumns = (fields = []) => {
  const result = [];
  const seen = new Set();

  fields.forEach((field) => {
    const key = getColumnKey(field);
    if (!key) return;
    const group = FIELD_EXPORT_COLUMN_MAP[key] || [key];
    group.forEach((col) => {
      if (seen.has(col)) return;
      seen.add(col);
      result.push(col);
    });
  });

  return result;
};

export const getExportColumnCount = (fields = []) => expandExportColumns(fields).length;

/** Keep selected field IDs whose expanded column count fits within the PDF limit. */
export const sliceFieldsToExportColumnLimit = (fields = [], limit) => {
  const selected = [];
  const seen = new Set();
  let count = 0;

  for (const field of fields) {
    const key = getColumnKey(field);
    if (!key) continue;

    const group = FIELD_EXPORT_COLUMN_MAP[key] || [key];
    const newCols = group.filter((col) => !seen.has(col));
    if (!newCols.length) continue;

    if (count + newCols.length > limit) {
      if (selected.length === 0) {
        selected.push(typeof field === "string" ? key : field);
      }
      break;
    }

    selected.push(typeof field === "string" ? key : field);
    newCols.forEach((col) => seen.add(col));
    count += newCols.length;
  }

  return selected;
};

/** Build a display label from an API column key (e.g. nextOfKinName → Next Of Kin Name). */
export const humanizeColumnKey = (key) => {
  if (!key || typeof key !== "string") return "";
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const compactColumnKey = (key) =>
  String(key || "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

const ENTITY_SUFFIX_PATTERN = /(worker|candidate|campaign|advert|status|actions|jobhistory)$/;

/** Align API labels like "NOK Name (Worker)" with field ids like nextOfKinName. */
const normalizeCompactForMatch = (value) =>
  compactColumnKey(String(value || "").replace(/\s*\([^)]*\)/g, ""))
    .replace(/nok/g, "nextofkin")
    .replace(/relationship/g, "relation")
    .replace(/refereerole/g, "refereeposition")
    .replace(/refereedetails/g, "refereecontact")
    .replace(/licence/g, "license")
    .replace(ENTITY_SUFFIX_PATTERN, "");

const exportGroupStem = (parentId) => {
  const group = FIELD_EXPORT_COLUMN_MAP[parentId];
  if (!group || group.length < 2) return "";
  const compacts = group.map(normalizeCompactForMatch).filter(Boolean);
  if (!compacts.length) return "";
  let stem = compacts[0];
  compacts.forEach((compact) => {
    while (stem && !compact.startsWith(stem)) {
      stem = stem.slice(0, -1);
    }
  });
  return stem.length >= 5 ? stem : "";
};

const CHILD_TO_PARENT_EXPORT_FIELD = Object.entries(FIELD_EXPORT_COLUMN_MAP).reduce(
  (acc, [parent, children]) => {
    const keys = [parent, ...children];
    keys.forEach((key) => {
      acc[key] = parent;
      acc[key.toLowerCase()] = parent;
      acc[humanizeColumnKey(key).toLowerCase()] = parent;
      acc[compactColumnKey(key)] = parent;
    });
    return acc;
  },
  {}
);

const colLookupKeys = (col) => {
  if (typeof col === "string") return col ? [col] : [];
  if (!col || typeof col !== "object") return [];
  return [col.key, col.id, col.field, col.name, col.label]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
};

const lookupMappedParent = (raw, selected) => {
  const compactRaw = normalizeCompactForMatch(raw);
  const parent =
    CHILD_TO_PARENT_EXPORT_FIELD[raw] ||
    CHILD_TO_PARENT_EXPORT_FIELD[raw.toLowerCase()] ||
    CHILD_TO_PARENT_EXPORT_FIELD[humanizeColumnKey(raw).toLowerCase()] ||
    CHILD_TO_PARENT_EXPORT_FIELD[compactColumnKey(raw)] ||
    CHILD_TO_PARENT_EXPORT_FIELD[compactRaw] ||
    Object.entries(CHILD_TO_PARENT_EXPORT_FIELD).find(([key]) => {
      const compactKey = normalizeCompactForMatch(key);
      if (compactKey.length < 6) return false;
      return (
        compactRaw === compactKey ||
        compactRaw.startsWith(compactKey) ||
        compactKey.startsWith(compactRaw)
      );
    })?.[1];

  if (parent && selected.includes(parent)) return parent;
  if (selected.includes(raw)) return raw;

  return selected.find((id) => normalizeCompactForMatch(id) === compactRaw) || "";
};

const parentGroupTokens = (parentId) => {
  const group = FIELD_EXPORT_COLUMN_MAP[parentId] || [parentId];
  const tokens = new Set();
  [parentId, ...group].forEach((key) => {
    const compact = normalizeCompactForMatch(key);
    if (!compact) return;
    tokens.add(compact);
    const stripped = compact
      .replace(/^upload/, "")
      .replace(/^is/, "")
      .replace(/uploaded$/, "")
      .replace(/upload$/, "");
    if (stripped.length >= 3) tokens.add(stripped);
  });
  return tokens;
};

const matchSelectedFieldMeta = (raw, selected, fields = []) => {
  if (!raw || !selected.length || !fields.length) return "";

  const compactRaw = compactColumnKey(raw);
  const lower = String(raw).trim().toLowerCase();

  for (const id of selected) {
    const field = fields.find((item) => item?.id === id);
    if (!field) continue;

    const label = String(field.label || "");
    const baseLabel = label.replace(/\s*\([^)]*\)\s*$/, "").trim();
    const candidates = [field.id, label, baseLabel];

    if (
      candidates.some((value) => {
        const text = String(value || "").trim();
        if (!text) return false;
        return (
          text === raw ||
          text.toLowerCase() === lower ||
          compactColumnKey(text) === compactRaw
        );
      })
    ) {
      return id;
    }
  }

  return "";
};

/** Map a preview/API column back to the selected parent field (e.g. NOK contact → nextOfKinName). */
export const resolveParentExportField = (col, selectedFields = [], fields = []) => {
  const selected = (selectedFields || []).map(getColumnKey).filter(Boolean);
  const keys = colLookupKeys(col);
  if (!keys.length) return "";

  for (const raw of keys) {
    const matched = lookupMappedParent(raw, selected);
    if (matched) return matched;
    const fromMeta = matchSelectedFieldMeta(raw, selected, fields);
    if (fromMeta) return fromMeta;
  }

  // Prefer exact available-field ids before fuzzy matching (e.g. userStatus must not match userName).
  for (const raw of keys) {
    const direct = fields.find((field) => field?.id === raw);
    if (direct?.id) return direct.id;
  }

  for (const raw of keys) {
    const compactRaw = normalizeCompactForMatch(raw);
    if (compactRaw.length < 3) continue;
    const fuzzy = selected.find((parentId) =>
      [...parentGroupTokens(parentId)].some(
        (token) =>
          token.length >= 4 &&
          (compactRaw === token || compactRaw.includes(token) || token.includes(compactRaw))
      )
    );
    if (fuzzy) return fuzzy;
  }

  return lookupMappedParent(keys[0], selected) || getColumnKey(col);
};

/** Parent field that owns this preview/API column (key match, then expanded-index fallback). */
export const getDisplayColumnParentId = (col, index, selectedFields = [], fields = []) => {
  const selected = (selectedFields || []).map(getColumnKey).filter(Boolean);
  const resolved = resolveParentExportField(col, selected, fields);
  if (resolved && selected.includes(resolved)) return resolved;
  return parentFieldForDisplayIndex(selected, index);
};

export const displayColumnBelongsToParent = (col, parentId, fields = []) =>
  Boolean(parentId) && resolveParentExportField(col, [parentId], fields) === parentId;

/** Which selected parent owns the preview column at this index (expanded order). */
export const parentFieldForDisplayIndex = (selectedFields = [], index = 0) => {
  const selected = (selectedFields || []).map(getColumnKey).filter(Boolean);
  let cursor = 0;
  for (const fieldId of selected) {
    const width = (FIELD_EXPORT_COLUMN_MAP[fieldId] || [fieldId]).length;
    if (index < cursor + width) return fieldId;
    cursor += width;
  }
  return selected[index] || "";
};

export const toApiDisplayColumns = (cols = []) => {
  if (!Array.isArray(cols)) return [];
  return cols
    .map((col) => {
      if (typeof col === "string") {
        const key = col.trim();
        return key ? { key, label: key } : null;
      }
      const key = String(col?.key || col?.name || col?.label || col?.id || "").trim();
      if (!key) return null;
      const label = String(col?.name || col?.label || col?.key || key).trim();
      return { key, label };
    })
    .filter(Boolean);
};

const compactKeysOverlap = (left, right) => {
  if (!left || !right) return false;
  if (left === right) return true;

  const shorter = left.length <= right.length ? left : right;
  const longer = left.length <= right.length ? right : left;

  if (shorter.length >= 6 && longer.startsWith(shorter)) return true;
  // short ids like soa/voc: "Qualification SOA" ends with soa, but voc must not steal vocIssued
  if (shorter.length >= 3 && shorter.length < 6 && longer.endsWith(shorter)) return true;
  return false;
};

const columnBelongsToExportGroup = (col, parentId, fields = []) => {
  if (displayColumnBelongsToParent(col, parentId, fields)) return true;
  const group = FIELD_EXPORT_COLUMN_MAP[parentId];
  if (!group?.length) return false;

  if (
    group.some((exportKey) => columnMatchesExportKey(col, exportKey, parentId, fields))
  ) {
    return true;
  }

  const stem = exportGroupStem(parentId);
  const tokens = new Set();
  if (stem) tokens.add(stem);
  group.forEach((key) => {
    const compact = normalizeCompactForMatch(key);
    if (compact.length >= 6) tokens.add(compact);
    const stripped = compact.replace(/^upload/, "").replace(/uploaded$/, "");
    if (stripped.length >= 6) tokens.add(stripped);
  });

  return colLookupKeys(col).some((raw) => {
    const compact = normalizeCompactForMatch(raw);
    return [...tokens].some((token) => compactKeysOverlap(compact, token));
  });
};

const columnMatchesExportKey = (col, exportKey, parentId, fields = []) => {
  const compactExport = normalizeCompactForMatch(exportKey);
  const compactParent = normalizeCompactForMatch(parentId);
  const keys = colLookupKeys(col);
  if (!keys.length) return false;

  if (
    keys.some((raw) => {
      const compact = normalizeCompactForMatch(raw);
      if (compactKeysOverlap(compact, compactExport)) return true;
      return exportKey === parentId && compactKeysOverlap(compact, compactParent);
    })
  ) {
    return true;
  }

  const field = fields.find((item) => item?.id === exportKey);
  const parentField =
    exportKey === parentId ? fields.find((item) => item?.id === parentId) : null;
  const labels = [field?.label, parentField?.label].filter(Boolean);
  if (!labels.length) return false;

  return keys.some((raw) => {
    const compact = normalizeCompactForMatch(raw);
    return labels.some((label) => {
      const labelCompact = normalizeCompactForMatch(label);
      const baseLabel = normalizeCompactForMatch(
        String(label).replace(/\s*\([^)]*\)\s*$/, "")
      );
      return compactKeysOverlap(compact, labelCompact) || compactKeysOverlap(compact, baseLabel);
    });
  });
};

/** Build preview columns in the selected/arranged parent field order. */
export const orderDisplayColumnsBySelectedFields = (
  cols = [],
  selectedFields = [],
  fields = []
) => {
  const display = toApiDisplayColumns(cols);
  const parents = (selectedFields || []).map(getColumnKey).filter(Boolean);
  if (!parents.length) return display;

  const used = new Set();
  const takeMatch = (predicate) => {
    const matchIndex = display.findIndex((col, index) => !used.has(index) && predicate(col));
    if (matchIndex < 0) return null;
    used.add(matchIndex);
    return display[matchIndex];
  };

  const ordered = parents.flatMap((parentId) => {
    const exportKeys = FIELD_EXPORT_COLUMN_MAP[parentId] || [parentId];
    const group = exportKeys.map((exportKey) =>
      takeMatch((col) => columnMatchesExportKey(col, exportKey, parentId, fields))
    );

    exportKeys.forEach((_, index) => {
      if (group[index]) return;
      group[index] = takeMatch((col) => columnBelongsToExportGroup(col, parentId, fields));
    });

    if (exportKeys.length > 1) {
      display.forEach((col, index) => {
        if (used.has(index)) return;
        if (!columnBelongsToExportGroup(col, parentId, fields)) return;
        used.add(index);
        group.push(col);
      });
    } else {
      group.forEach((_, index) => {
        if (group[index]) return;
        group[index] = takeMatch((col) =>
          columnMatchesExportKey(col, parentId, parentId, fields)
        );
      });
    }

    return group.filter(Boolean);
  });

  display.forEach((col, index) => {
    if (!used.has(index)) ordered.push(col);
  });

  if (ordered.length) return ordered;

  return parents.flatMap((parentId) => {
    const exportKeys = FIELD_EXPORT_COLUMN_MAP[parentId] || [parentId];
    return exportKeys.map((exportKey) => {
      const field =
        fields.find((item) => item?.id === exportKey) ||
        fields.find((item) => item?.id === parentId);
      return {
        key: exportKey,
        label: field?.label || humanizeColumnKey(exportKey),
      };
    });
  });
};

const toColumnMeta = (col) => {
  const [mapped] = toApiDisplayColumns([col]);
  return mapped || null;
};

/**
 * Build columns from generate API `cols`.
 * - expand=true  → use full API cols (preview screen)
 * - expand=false → only keys the user selected (create Report Preview)
 */
export const getResponseColumns = (data, fallbackColumns = [], { expand = false } = {}) => {
  const requestedKeys = (fallbackColumns || []).map(getColumnKey).filter(Boolean);
  const apiCols = Array.isArray(data?.cols) ? data.cols.map(toColumnMeta).filter(Boolean) : [];

  if (expand) {
    if (apiCols.length) return apiCols;
    return requestedKeys.map((key) => ({ key, label: humanizeColumnKey(key) }));
  }

  if (!requestedKeys.length) return [];

  const apiByKey = new Map(apiCols.map((col) => [col.key, col]));
  return requestedKeys.map(
    (key) => apiByKey.get(key) || { key, label: humanizeColumnKey(key) }
  );
};

export const getResponseColumnLabel = (col) => {
  if (col && typeof col === "object") {
    return col.label || col.name || humanizeColumnKey(col.key || "");
  }
  return humanizeColumnKey(col);
};

export const readTotalRows = (data, fallback) => {
  const total =
    data?.summary?.totalRecords ??
    data?.total ??
    data?.totalCount ??
    data?.count ??
    data?.totalRows;
  return typeof total === "number" ? total : fallback;
};
